import { useState, useEffect, useCallback } from 'react';
import { getCachedData, setCachedData, clearCacheKey } from '../services/cacheService';

interface UseCachedDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  clearCache: () => Promise<void>;
  lastRefreshed: Date | null;
}

/**
 * React hook for cached data management
 * @param cacheKey - Cache key for storing/retrieving data
 * @param fetchFn - Function to fetch fresh data
 * @returns Object with data, loading state, error, refresh function, and cache metadata
 */
export function useCachedData<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>
): UseCachedDataReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cached = await getCachedData<T>(cacheKey);
        if (cached) {
          setData(cached.data);
          setLastRefreshed(cached.lastRefreshed);
          setLoading(false);
          return;
        }
      }

      // Fetch fresh data
      const freshData = await fetchFn();
      setData(freshData);
      
      // Cache the data
      await setCachedData(cacheKey, freshData);
      
      // Update lastRefreshed
      setLastRefreshed(new Date());
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      console.error(`[useCachedData] Error loading data for key "${cacheKey}":`, error);
      // On error, try to load from cache if available (fallback)
      if (!forceRefresh) {
        const cached = await getCachedData<T>(cacheKey);
        if (cached) {
          setData(cached.data);
          setLastRefreshed(cached.lastRefreshed);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [cacheKey, fetchFn]);

  const refresh = useCallback(async () => {
    await loadData(true);
  }, [loadData]);

  const clearCache = useCallback(async () => {
    await clearCacheKey(cacheKey);
    setData(null);
    setLastRefreshed(null);
  }, [cacheKey]);

  useEffect(() => {
    // Only load on mount or when cacheKey changes, not when fetchFn changes
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]); // Only depend on cacheKey, not loadData

  return {
    data,
    loading,
    error,
    refresh,
    clearCache,
    lastRefreshed,
  };
}
